<script lang="ts">
  import Button from '../../Button.svelte';
  import { createMutation, createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { ProjectContentType, type HackathonProject } from '$shared/lib';
  import { client } from '$lib/api/Client';
  import ThumbsUpIcon from '$icons/ThumbsUpIcon.svelte';
  import { toastCustom } from '$utils/toasts';
  import { ClientResponseError } from '$lib/api/ClientResponseError';
  import { cn } from '$lib/utils/misc';
  import type { User } from '$routes/(app)/+layout.server';

  export let project: HackathonProject;
  export let user: User | null;

  const queryClient = useQueryClient();
  const likesQueryKey = ['project', project.id, 'likes'];

  // Query to get current like status
  const likesQuery = createQuery({
    queryKey: likesQueryKey,
    queryFn: () =>
      client.projects.retrieveProjectLikes({
        contentType: ProjectContentType.PROJECT,
        contentId: project.id,
      }),
  });

  // Mutation to handle liking/unliking
  const likeMutation = createMutation({
    mutationFn: async () => {
      if ($likesQuery.data?.likedByCurrentUser) {
        return client.projects.deleteProjectLike({
          likableContentType: ProjectContentType.PROJECT,
          contentId: project.id,
        });
      }

      return client.projects.createProjectLike({
        likedContentType: ProjectContentType.PROJECT,
        contentId: project.id,
      });
    },
    onMutate: async () => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: likesQueryKey });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(likesQueryKey);

      // Optimistically update the like status
      if ($likesQuery.data) {
        queryClient.setQueryData(likesQueryKey, {
          likeCount: $likesQuery.data.likedByCurrentUser
            ? $likesQuery.data.likeCount - 1
            : $likesQuery.data.likeCount + 1,
          likedByCurrentUser: !$likesQuery.data.likedByCurrentUser,
        });
      }

      return { previousData };
    },
    onError: (error, _, context) => {
      // Roll back optimistic update on error
      if (context?.previousData) {
        queryClient.setQueryData(likesQueryKey, context.previousData);
      }

      if (error instanceof ClientResponseError) {
        toastCustom({
          title: 'Error',
          description: error.message,
          negative: true,
          position: 'bottom-center',
        });
      } else {
        toastCustom({
          title: 'Error',
          description: 'An error occurred while updating like status.',
          negative: true,
          position: 'bottom-center',
        });
      }
    },
    onSettled: () => {
      // Refetch after error or success
      queryClient.invalidateQueries({ queryKey: likesQueryKey });
    },
  });

  async function handleLikeClick() {
    await $likeMutation.mutateAsync();
  }
</script>

{#if $likesQuery.isSuccess}
  <Button
    size="sm"
    transparent
    class={cn('!px-0', $$props.class)}
    disabled={!user || $likesQuery.isPending || $likeMutation.isPending}
    on:click={handleLikeClick}
  >
    <ThumbsUpIcon
      class={`${$likesQuery.data?.likedByCurrentUser ? 'text-mint-dark-11' : ''} h-5 w-5`}
    />
    {#if $likesQuery.data?.likeCount > 0}
      <span class:text-mint-dark-11={$likesQuery.data?.likedByCurrentUser} class="text-lg">
        {$likesQuery.data?.likeCount}
      </span>
    {:else}
      <span class="text-lg">Like</span>
    {/if}
  </Button>
{/if}
